import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '@/components/feature/SuperAdminLayout';
import SAHeader from '@/components/feature/SAHeader';
import { requireSuperAdmin } from '@/middleware/superAdminGuard';

// ── Types ──────────────────────────────────────────────────────────────────────
type DeploymentStatus = 'online' | 'offline' | 'warning' | 'pending';

interface DeploymentItem {
  id: string;
  name: string;
  status: DeploymentStatus;
  updatedAt: string;
  notes: string;
}

interface TimelineStep {
  label: string;
  icon: string;
  active: boolean;
  description: string;
}

// ── Status config ──────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<DeploymentStatus, { color: string; bg: string; border: string; dot: string; label: string }> = {
  online:  { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-400', label: 'Online' },
  offline: { color: 'text-rose-700',    bg: 'bg-rose-50',    border: 'border-rose-200',    dot: 'bg-rose-400',    label: 'Offline' },
  warning: { color: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200',   dot: 'bg-amber-400',   label: 'Warning' },
  pending: { color: 'text-slate-500',   bg: 'bg-slate-50',   border: 'border-slate-200',   dot: 'bg-slate-300',   label: 'Pending' },
};

// ── Mock deployment data ───────────────────────────────────────────────────────
const getInitialData = (): DeploymentItem[] => [
  { id: 'frontend',     name: 'Frontend Build',    status: 'warning', updatedAt: new Date().toISOString(), notes: 'Build ok. CDN propagation pending. Assets served from dev origin.' },
  { id: 'vps-storage',  name: 'VPS Storage',        status: 'pending', updatedAt: new Date().toISOString(), notes: 'VPS provisioning not started. Using Supabase Storage in the meantime.' },
  { id: 'php-api',      name: 'PHP API',             status: 'pending', updatedAt: new Date().toISOString(), notes: 'API layer not deployed. All logic currently on Supabase Edge Functions.' },
  { id: 'email-worker', name: 'Email Worker',        status: 'offline', updatedAt: new Date().toISOString(), notes: 'Email queue has 50+ pending items. Worker Edge Function not deployed yet.' },
  { id: 'whatsapp',     name: 'WhatsApp Worker',     status: 'offline', updatedAt: new Date().toISOString(), notes: 'WhatsApp queue exists but processor not implemented. Messages will not be delivered.' },
  { id: 'ssl',          name: 'SSL',                 status: 'warning', updatedAt: new Date().toISOString(), notes: 'Self-signed cert active. Production Let\'s Encrypt cert not yet configured.' },
  { id: 'domain',       name: 'Domain',              status: 'warning', updatedAt: new Date().toISOString(), notes: 'Custom domain requests pending. Currently serving from Readdy subdomain.' },
  { id: 'cron-jobs',    name: 'Cron Jobs',           status: 'warning', updatedAt: new Date().toISOString(), notes: 'No cron scheduler configured. Email reminders and cleanup jobs are inactive.' },
  { id: 'backups',      name: 'Backups',             status: 'warning', updatedAt: new Date().toISOString(), notes: 'No automated backup schedule. Supabase PITR not enabled. Manual exports only.' },
];

// ── Timeline steps ─────────────────────────────────────────────────────────────
const TIMELINE_STEPS: TimelineStep[] = [
  { label: 'Development',      icon: 'ri-code-s-slash-line', active: true,  description: 'Local + Readdy dev environment active' },
  { label: 'QA Testing',       icon: 'ri-test-tube-line',    active: false, description: 'Staging & QA environment pending' },
  { label: 'Backend Connected',icon: 'ri-database-2-line',   active: false, description: 'PHP API + Workers integration pending' },
  { label: 'Production Ready', icon: 'ri-rocket-2-line',     active: false, description: 'Domain, SSL, CDN and workers needed' },
];

// ── Overall KPI ────────────────────────────────────────────────────────────────
function computeSummary(items: DeploymentItem[]) {
  const online  = items.filter((i) => i.status === 'online').length;
  const warning = items.filter((i) => i.status === 'warning').length;
  const offline = items.filter((i) => i.status === 'offline').length;
  const pending = items.filter((i) => i.status === 'pending').length;
  const overall: DeploymentStatus = offline > 0 ? 'offline' : warning > 0 ? 'warning' : pending > 0 ? 'pending' : 'online';
  return { online, warning, offline, pending, overall, total: items.length };
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function SummaryKPIs({ items }: { items: DeploymentItem[] }) {
  const s = computeSummary(items);
  const overallCfg = STATUS_CONFIG[s.overall];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
      {/* Overall */}
      <div className={`rounded-xl border ${overallCfg.border} ${overallCfg.bg} p-4`}>
        <p className="text-xs text-slate-500 mb-1">Overall Status</p>
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${overallCfg.dot} ${s.overall === 'online' ? 'animate-pulse' : ''}`}></span>
          <span className={`text-lg font-bold ${overallCfg.color}`}>{overallCfg.label}</span>
        </div>
      </div>

      <KpiCard label="Online" count={s.online} total={s.total} color="emerald" icon="ri-checkbox-circle-fill" />
      <KpiCard label="Warning" count={s.warning} total={s.total} color="amber" icon="ri-error-warning-fill" />
      <KpiCard label="Offline" count={s.offline} total={s.total} color="rose" icon="ri-close-circle-fill" />
      <KpiCard label="Pending" count={s.pending} total={s.total} color="slate" icon="ri-time-fill" />
    </div>
  );
}

function KpiCard({ label, count, total, color, icon }: { label: string; count: number; total: number; color: string; icon: string }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <div className="flex items-center justify-between">
        <span className={`text-lg font-bold text-${color}-600`}>{count}</span>
        <span className="w-5 h-5 flex items-center justify-center">
          <i className={`${icon} text-${color}-400 text-sm`}></i>
        </span>
      </div>
      <div className="mt-2 w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full bg-${color}-400 rounded-full transition-all duration-500`} style={{ width: `${pct}%` }}></div>
      </div>
    </div>
  );
}

function DeploymentCard({ item, onRefresh, onViewLogs }: { item: DeploymentItem; onRefresh: (id: string) => void; onViewLogs: (id: string) => void }) {
  const cfg = STATUS_CONFIG[item.status];
  const time = new Date(item.updatedAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 hover:border-slate-300 transition-all">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className={`w-3 h-3 rounded-full ${cfg.dot} ${item.status === 'online' ? 'animate-pulse' : ''} shrink-0`}></span>
          <h4 className="text-sm font-semibold text-slate-800">{item.name}</h4>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color} whitespace-nowrap`}>
          {cfg.label}
        </span>
      </div>

      <p className="text-xs text-slate-500 mb-3 leading-relaxed">{item.notes}</p>

      <div className="flex items-center justify-between">
        <span className="text-[11px] text-slate-400 flex items-center gap-1">
          <i className="ri-time-line text-[11px]"></i>
          Last checked: {time}
        </span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onRefresh(item.id)}
            className="flex items-center gap-1 px-2.5 py-1 text-[11px] text-sky-700 hover:text-sky-800 hover:bg-sky-50 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line text-[11px]"></i>
            Refresh
          </button>
          <button
            onClick={() => onViewLogs(item.id)}
            className="flex items-center gap-1 px-2.5 py-1 text-[11px] text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-list-3-line text-[11px]"></i>
            View Logs
          </button>
        </div>
      </div>
    </div>
  );
}

function DeploymentTimeline() {
  const activeIndex = TIMELINE_STEPS.findIndex((s) => !s.active);
  const lastActiveIdx = activeIndex === -1 ? TIMELINE_STEPS.length - 1 : activeIndex - 1;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6">
      <h3 className="text-sm font-semibold text-slate-800 mb-5 flex items-center gap-2">
        <i className="ri-road-map-line text-slate-500"></i>
        Deployment Pipeline
      </h3>

      <div className="relative">
        {/* Connecting line */}
        <div className="absolute top-5 left-[5%] right-[5%] h-0.5 bg-slate-200 hidden md:block"></div>
        {/* Active portion */}
        {lastActiveIdx >= 0 && (
          <div
            className="absolute top-5 left-[5%] h-0.5 bg-gradient-to-r from-sky-500 to-sky-400 hidden md:block transition-all duration-700"
            style={{ width: `calc((85% / ${TIMELINE_STEPS.length - 1}) * ${lastActiveIdx} + (85% / ${TIMELINE_STEPS.length - 1}) / 2)` }}
          ></div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0">
          {TIMELINE_STEPS.map((step, idx) => (
            <div key={step.label} className="flex md:flex-col items-center md:items-center gap-4 md:gap-0 relative z-10">
              {/* Circle */}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 shrink-0 ${
                step.active
                  ? 'bg-sky-50 border-sky-400 text-sky-600'
                  : 'bg-white border-slate-200 text-slate-300'
              }`}>
                <i className={`${step.icon} text-sm`}></i>
              </div>

              {/* Label + description */}
              <div className="md:text-center md:mt-3">
                <p className={`text-sm font-semibold ${step.active ? 'text-sky-700' : 'text-slate-400'}`}>
                  {step.label}
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5 max-w-[140px] mx-auto leading-tight">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LogsPlaceholder({ open, serviceName, onClose }: { open: boolean; serviceName: string; onClose: () => void }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl border border-slate-200 shadow-lg w-full max-w-lg mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <i className="ri-file-list-3-line text-slate-500"></i>
            Logs — {serviceName}
          </h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="bg-slate-50 rounded-lg border border-slate-100 p-5 text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-slate-100 flex items-center justify-center">
            <i className="ri-inbox-line text-slate-400 text-xl"></i>
          </div>
          <p className="text-sm font-medium text-slate-600 mb-1">Log viewer not connected</p>
          <p className="text-xs text-slate-400 leading-relaxed">
            Real-time log streaming will be available once the logging infrastructure is set up.
            This is a visual placeholder for the deployment monitoring module.
          </p>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 border border-slate-200 hover:bg-slate-50 rounded-lg cursor-pointer whitespace-nowrap transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function DeploymentPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<DeploymentItem[]>(getInitialData());
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [logsTarget, setLogsTarget] = useState<{ id: string; name: string } | null>(null);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      const ok = await requireSuperAdmin();
      if (!ok) { navigate('/login'); return; }
    };
    init();
  }, [navigate]);

  const handleRefreshAll = useCallback(() => {
    setItems(getInitialData());
    setLastRefreshed(new Date());
  }, []);

  const handleRefreshItem = useCallback((id: string) => {
    setRefreshingId(id);
    setTimeout(() => {
      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, updatedAt: new Date().toISOString() } : item
        )
      );
      setRefreshingId(null);
    }, 600);
  }, []);

  const summary = computeSummary(items);

  return (
    <SuperAdminLayout>
      <SAHeader
        title="Deployment Status"
        subtitle="Infrastructure health overview — development to production pipeline"
        actions={
          <div className="flex items-center gap-3">
            {/* Summary pill */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${STATUS_CONFIG[summary.overall].bg} ${STATUS_CONFIG[summary.overall].border} ${STATUS_CONFIG[summary.overall].color}`}>
              <span className={`w-2 h-2 rounded-full ${STATUS_CONFIG[summary.overall].dot} ${summary.overall === 'online' ? 'animate-pulse' : ''}`}></span>
              {summary.overall === 'online' ? 'All Systems Operational' : summary.overall === 'warning' ? 'Attention Needed' : summary.overall === 'offline' ? 'Services Down' : 'Deployment Pending'}
            </div>

            <button
              onClick={handleRefreshAll}
              disabled={!!refreshingId}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-gradient-to-r from-sky-600 to-sky-500 hover:from-sky-700 hover:to-sky-600 rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              <i className={`ri-refresh-line text-sm ${refreshingId ? 'animate-spin' : ''}`}></i>
              Refresh All
            </button>
          </div>
        }
      />

      <div className="px-4 md:px-8 pt-5 pb-8">
        {/* Last refreshed */}
        <p className="text-slate-400 text-xs mb-4 flex items-center gap-1.5">
          <i className="ri-time-line text-xs"></i>
          Last refreshed: {lastRefreshed.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </p>

        {/* KPIs */}
        <SummaryKPIs items={items} />

        {/* Timeline */}
        <DeploymentTimeline />

        {/* Deployment Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <DeploymentCard
              key={item.id}
              item={item}
              onRefresh={handleRefreshItem}
              onViewLogs={(id) => {
                const found = items.find((i) => i.id === id);
                setLogsTarget({ id, name: found?.name ?? id });
              }}
            />
          ))}
        </div>

        {/* Logs placeholder modal */}
        <LogsPlaceholder
          open={!!logsTarget}
          serviceName={logsTarget?.name ?? ''}
          onClose={() => setLogsTarget(null)}
        />
      </div>
    </SuperAdminLayout>
  );
}